import { Component, Prop, VNode, h } from '@stencil/core';
import { Hyperlink, getHyperLinkObject } from '../../../core/interfaces/Hyperlink';
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'utils/collection';

/**
 * @tag `<ads-card-image>`
 * @hidden
 */
@Component({
  tag: 'ads-card-image',
  styleUrl: 'ads-card-image.scss',
  shadow: true,
})
export class AdsCardImage {
  /**
   * If set, the value will be used as the background-image location (optional)
   */
  @Prop() src?: string;

  /**
   * If set, value will be used as overlay image on src image (optional)
   */
  @Prop() overlay?: string;

  /**
   * Contains a URL (fragment).
   * If this property is set, an anchor tag will be rendered (optional)
   */
  @Prop({ reflect: true }) link?: Hyperlink;

  /**
   * The value for the aria-label (optional)
   */
  @Prop() srLabel?: string;

  /**
   * The animation effect value (optional)
   */
  @Prop() effect?: string;

  protected render(): VNode {
    const linkObj = getHyperLinkObject(this.link);

    const Tag = !isNullOrUndefined(linkObj) ? 'a' : 'div';

    const attributes: {
      href?: string;
      hrefLang?: string;
      rel?: string;
      target?: string;
      onClick?: (evt: MouseEvent) => void;
      'data-track'?: string;
      'data-track-value'?: string;
    } =
      Tag === 'a'
        ? {
            href: linkObj?.href,
            hrefLang: linkObj?.hreflang || null,
            rel: linkObj?.rel || null,
            target: linkObj?.target || null,
            onClick: linkObj?.onClick || null,
            'data-track': 'true',
            'data-track-value': this.srLabel,
          }
        : {};

    // Generate the classes
    const classes = ['c-card-image__wrapper'];
    if (!isNullOrUndefinedOrEmpty(this.src)) {
      classes.push('has_background');
    }

    // Generate the image styles
    const styles = {
      backgroundImage: !isNullOrUndefinedOrEmpty(this.src) ? `url(${this.src})` : null,
    };

    return (
      <Tag class={classes.join(' ')} {...attributes}>
        {!isNullOrUndefinedOrEmpty(this.src) ? (
          <div class={`c-card-image__background ${!isNullOrUndefinedOrEmpty(this.effect) ? this.effect : ''}`} style={styles}>
            {!isNullOrUndefinedOrEmpty(this.srLabel) ? <span role="img" aria-label={this.srLabel} /> : null}
            <div>
              {!isNullOrUndefinedOrEmpty(this.overlay) ? (
                <div class={'c-card-image__overlay-image'}>
                  <img src={this.overlay} alt="placeholder" />
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <picture class="c-card-image__image">
            <slot />
          </picture>
        )}
      </Tag>
    ) as VNode;
  }
}
